// QUANTAS PÁGINAS TEM — `T8.11`, e o achado do relatório é literal: *"Três telas, três contagens de
// página para o mesmo documento."*
//
// Medido em produção em 25/09/2026: a única história publicada aparecia na prateleira como
// **"0 páginas"**. A causa é de uma linha e é consequência do `T6.6`.
//
// Até o T6.6 a ficha da biblioteca carregava as páginas DENTRO dela, e `ficha.paginas.length` era
// a contagem. O T6.6 tirou as páginas de lá — cada página, e cada imagem dela, virou documento
// irmão em `biblioteca/{id}/paginas` — porque o Firestore recusa documento acima de 1.048.487
// bytes. E a `firestore.rules` passou a RECUSAR ficha que traga `paginas`, para a separação não ser
// decoração.
//
// Três telas leram isso de três jeitos:
//
//   inicio/feito-aqui.ts      `quantasPaginas`                     -> certo
//   biblioteca/ler-historia   `paginas.length || quantasPaginas`   -> certo
//   biblioteca/biblioteca     `paginas?.length ?? 0`               -> SEMPRE 0
//
// Nenhuma das três estava "errada" quando foi escrita. A terceira ficou errada quando o formato do
// dado mudou embaixo dela, e ninguém notou porque `?? 0` transforma ausência em número: a tela não
// quebra, ela mente baixinho. É o mesmo motivo pelo qual esta contagem agora mora num lugar só.

/** Uma ficha da biblioteca, vista por quem só quer saber o tamanho dela. */
type FichaComTamanho = {
  paginas?: unknown;
  quantasPaginas?: unknown;
};

/**
 * Quantas páginas a ficha declara.
 *
 * `paginas` vem primeiro só por causa de ficha ANTIGA, publicada antes de o T6.6 tirar as páginas
 * do documento. Ficha nova não pode ter o campo — a regra recusa a escrita —, então na prática o
 * caminho que roda é o `quantasPaginas`.
 */
export function paginasDaFicha(ficha: unknown): number {
  const f = (ficha || {}) as FichaComTamanho;
  if (Array.isArray(f.paginas) && f.paginas.length > 0) return f.paginas.length;
  if (typeof f.quantasPaginas === "number" && f.quantasPaginas > 0) return f.quantasPaginas;
  return 0;
}

/**
 * O rótulo, ou `null` quando não há número para mostrar.
 *
 * Devolver `null` em vez de `"0 páginas"` é a metade do conserto que vale mais: uma ficha velha
 * sem o campo não sabe o próprio tamanho, e **"0 páginas" afirma que a história está vazia** — que
 * é uma coisa diferente e pior. Quem não sabe, não diz.
 */
export function rotuloDePaginas(quantas: number): string | null {
  if (!Number.isFinite(quantas) || quantas <= 0) return null;
  return quantas === 1 ? "1 página" : `${quantas} páginas`;
}
